import React, { memo, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Link, Button, Icon, SearchBar, Tooltip } from '@Sage/hoi-poi-ui';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { connect } from 'react-redux';
import SearchRow from 'components/SearchRow';
import { SALESORDERS } from 'constants/Entities';
import {
    WidgetLayout,
    WidgetHeaderLayout,
    WidgetContentLayout,
} from 'containers/components/widgets/Layouts';
import SalesOrdersProductLinesTotalsFooter from 'containers/SalesOrders/components/SalesOrdersProductLinesTotalsFooter';
import { getShowSalesOrderFooterAmounts } from 'containers/SalesOrders/utils/showSalesOrderFooterAmounts';
import Context from 'managers/Context';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getSmartFieldProps, isAddProductsButtonLocked } from 'utils/fm/fields';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';

import AddProductsModal from './AddProductsModal';
import { resolveProductLinesEnvironment } from './environment';
import ProductLinesTable from './ProductLinesTable';
import './styles.scss';

const mapStateToProps = (state) => {
    return {
        standardFieldsSchemaSalesOrders:
            state.config?.standardFieldsSchemaMap?.[SALESORDERS.extraFieldName],
        quoteProductByEnvironment: state?.config?.permission?.quoteProductByEnvironment,
        showSalesOrderFooterAmounts: getShowSalesOrderFooterAmounts(state.config?.userData),
        useTaxesSalesOrders: getBackendBoolean(
            state?.config?.userData?.useTaxesSalesOrders || false,
        ),
    };
};

const ProductLines = memo(
    ({
        changeField,
        data,
        errors,
        field,
        getError,
        standardFieldsSchemaSalesOrders,
        quoteProductByEnvironment,
        configMap: parentConfigMap,
        isNewEntity,
        idCurrency,
        idEnvironment,
        showSalesOrderFooterAmounts,
        useTaxesSalesOrders,
        copilotModifiedProductLines,
    }) => {
        const tableRef = useRef(null);
        const widgetRef = useRef(null);
        const innerProductsSearch = useRef(null);
        const addProductsModalRef = useRef(null);

        const [checkedProducts, setCheckedProducts] = useState([]);

        useEffect(() => {
            if (errors.products) widgetRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, [errors]);

        const disableEditSalesOrdersLines = useMemo(() => {
            return isAddProductsButtonLocked(
                getSmartFieldProps({
                    field: standardFieldsSchemaSalesOrders?.addProductsButton,
                    configMap: parentConfigMap,
                    data,
                    isNewEntity,
                }),
            );
        }, [
            data,
            isNewEntity,
            parentConfigMap,
            standardFieldsSchemaSalesOrders?.addProductsButton,
        ]);

        const effectiveIdEnvironment = useMemo(() => {
            return resolveProductLinesEnvironment({ idEnvironment, parentData: data });
        }, [data, idEnvironment]);

        const openAddProductModal = useCallback(() => {
            addProductsModalRef.current.open();
        }, []);

        const onAddProduct = useCallback(
            (product, quantity) => {
                changeField('product')({ product, quantity });
            },
            [changeField],
        );

        const onAddProducts = useCallback(
            (products) => {
                changeField('product')(products);
            },
            [changeField],
        );

        const handleOnRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const tableErrors = useMemo(() => {
            const productErrors =
                errors.products && typeof errors.products === 'object' ? errors.products : {};

            const humanErrors = Object.keys(productErrors).reduce((newErrors, errorKey) => {
                newErrors[errorKey] = {
                    rows:
                        Object.keys(productErrors[errorKey]?.rows || {}).reduce(
                            (errorRow, rowKey) => {
                                const row = productErrors[errorKey]?.rows[rowKey];
                                errorRow[rowKey] = {
                                    // Boolean error: red border only, no message under the cell
                                    error: row.error === 'mandatory' ? true : getError(row.error),
                                    colId: row.colId,
                                };
                                return errorRow;
                            },
                            {},
                        ) || {},
                };
                return newErrors;
            }, {});

            // Live minStep feedback: reflect `_minStepError` on the quantity cell as the
            // user edits quantity or switches UoM, without waiting for save. Passing a
            // truthy error (space) turns the cell red without surfacing a message.
            const quantityRows = humanErrors.quantity?.rows ? { ...humanErrors.quantity.rows } : {};
            let hasQuantityRows = Object.keys(quantityRows).length > 0;

            (data?.products || []).forEach((product, rowIndex) => {
                if (product?._minStepError) {
                    // `persistent: true` keeps SectionListCrud's auto-clear from wiping the
                    // flag while the cell still holds a value. `error: ' '` marks the cell
                    // red without surfacing a message.
                    quantityRows[rowIndex] = {
                        error: ' ',
                        colId: 'quantity',
                        persistent: true,
                    };
                    hasQuantityRows = true;
                } else if (quantityRows[rowIndex]?.persistent) {
                    delete quantityRows[rowIndex];
                }
            });

            if (hasQuantityRows) {
                humanErrors.quantity = { rows: quantityRows };
            } else if (humanErrors.quantity && Object.keys(quantityRows).length === 0) {
                delete humanErrors.quantity;
            }

            return Object.keys(humanErrors).length ? humanErrors : null;
        }, [errors, data?.products, getError]);

        const handleSetCheckedProducts = useCallback(
            (checkedRows) => {
                setCheckedProducts(checkedRows);
            },
            [setCheckedProducts],
        );

        const tableContainer = useMemo(() => {
            return (
                <div className="sales-order-product-lines__crud-widget__content__table">
                    <ProductLinesTable
                        data={data.products}
                        handleOnRef={handleOnRef}
                        crudSchema={field.schema}
                        errors={tableErrors}
                        changeField={changeField}
                        handleSetCheckedProducts={handleSetCheckedProducts}
                        parentData={data}
                        parentConfigMap={parentConfigMap}
                        isNewEntity={isNewEntity}
                        disableEditSalesOrdersLines={disableEditSalesOrdersLines}
                        idCurrency={idCurrency}
                        idEnvironment={effectiveIdEnvironment}
                        copilotModifiedProductLines={copilotModifiedProductLines}
                    />
                </div>
            );
        }, [
            data,
            handleOnRef,
            field.schema,
            tableErrors,
            changeField,
            handleSetCheckedProducts,
            parentConfigMap,
            isNewEntity,
            disableEditSalesOrdersLines,
            idCurrency,
            effectiveIdEnvironment,
            copilotModifiedProductLines,
        ]);

        const onDeleteProductLines = useCallback(() => {
            changeField('removeProducts')(checkedProducts);
            setCheckedProducts([]);
            if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
        }, [checkedProducts, changeField]);

        const onClearSelection = useCallback(() => {
            setCheckedProducts([]);
            if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
        }, []);

        const loadProductOptions = useCallback(
            (searchText) => {
                return Context.entityManager
                    .getEntitiesManager(SALESORDERS)
                    .getSalesOrdersProductLines(
                        '-1',
                        searchText,
                        data.idRate || '-1',
                        data.idCompany?.value || '-1',
                        quoteProductByEnvironment
                            ? data.idBranch?.value || data.idBranch || '-1'
                            : '-1',
                    )
                    .then((results) => {
                        innerProductsSearch.current = [];
                        return results.reduce((obj, result) => {
                            result?.products?.forEach((product) => {
                                if (product.isProductFolder) return obj;
                                innerProductsSearch.current.push(product);
                                obj.push({
                                    label: product.name,
                                    value: product.id,
                                    description: product.description,
                                    price: formatDecimalByConfig({
                                        value: product.finalprice,
                                        type: DECIMAL_TYPES.CURRENCY_PRICES,
                                        idEnvironment: effectiveIdEnvironment,
                                        idCurrency,
                                        entityName: 'PRODUCTS',
                                        fieldId: 'ProductPrice',
                                    }),
                                });
                            });
                            return obj;
                        }, []);
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            },
            [
                data.idBranch,
                data.idCompany?.value,
                data.idRate,
                effectiveIdEnvironment,
                idCurrency,
                quoteProductByEnvironment,
            ],
        );

        const onClickProduct = useCallback(
            (value) => {
                const product = innerProductsSearch.current?.find((p) => p.id === value);
                onAddProduct(product, 1);
                logEvent({ event: SALESORDERS.trueName, functionality: 'productQuickEntry' });
            },
            [onAddProduct],
        );

        const customProductOption = useCallback(
            (item, getMatchingCharacters) => {
                if (!item.label || !item.value) return;

                const subtitles = Object.keys(item).reduce((arr, key) => {
                    if (key !== 'label' && key !== 'value') {
                        arr.push(item[key]);
                    }
                    return arr;
                }, []);

                return (
                    <SearchRow
                        className="fm-row-search__products"
                        onClick={() => onClickProduct(item.value)}
                        title={item.label}
                        subtitles={subtitles}
                        getMatchingCharacters={getMatchingCharacters}
                    />
                );
            },
            [onClickProduct],
        );

        const handleProductsModalOnRef = useCallback((ref) => {
            addProductsModalRef.current = ref;
        }, []);

        const widgetHeaderActions = useMemo(() => {
            const buttonProps = {
                size: 'small',
                color: 'primary',
                type: 'primary',
                onClick: openAddProductModal,
                children: getLiteral('label_add_lines'),
                isDisabled: disableEditSalesOrdersLines,
            };

            return disableEditSalesOrdersLines ? (
                <Tooltip content={getLiteral('label_cannot_add_lines')} placement="left">
                    <Button {...buttonProps} />
                </Tooltip>
            ) : (
                <Button {...buttonProps} />
            );
        }, [disableEditSalesOrdersLines, openAddProductModal]);

        const showLinesTotalsFooter = !showSalesOrderFooterAmounts;

        const widgetClassName = useMemo(() => {
            const classes = ['sales-order-product-lines__crud-widget'];
            if (errors?.products) classes.push('sales-order-product-lines__crud-widget--error');
            if (showLinesTotalsFooter) {
                classes.push('sales-order-product-lines__crud-widget--with-totals-footer');
            }
            return classes.join(' ');
        }, [errors.products, showLinesTotalsFooter]);

        return (
            <WidgetLayout
                className={widgetClassName}
                isDynamic={true}
                type="generalInfo"
                data={data}
            >
                <WidgetHeaderLayout>
                    <div ref={widgetRef} className="sales-order-product-lines__crud-widget__header">
                        <div className="sales-order-product-lines__crud-widget__header__actions">
                            {checkedProducts.length === 0 ? (
                                <div className="sales-order-product-lines__product-search">
                                    <SearchBar
                                        placeholder={getLiteral('placeholder_add_product')}
                                        loadOptions={loadProductOptions}
                                        customOption={customProductOption}
                                        actions={[
                                            {
                                                label: getLiteral('label_see_all_products'),
                                                onClick: openAddProductModal,
                                            },
                                        ]}
                                        defaultSearch=""
                                        size="small"
                                        isFuzzy
                                        isReadOnly={disableEditSalesOrdersLines}
                                    />
                                </div>
                            ) : (
                                <div className="sales-order-product-lines__crud-widget__header__title">
                                    <div className="sales-orders-product-lines__crud-widget__header__title-delete-number">
                                        {getLiteralWithParameters('label_lines_selected', [
                                            checkedProducts.length,
                                        ])}
                                    </div>
                                    <Icon name="delete" onClick={onDeleteProductLines} />
                                    <Link variation="primary" onClick={onClearSelection}>
                                        {getLiteral('label_clear_selection')}
                                    </Link>
                                </div>
                            )}

                            {widgetHeaderActions}
                        </div>
                    </div>
                </WidgetHeaderLayout>
                <WidgetContentLayout>
                    <div className="sales-order-product-lines__crud-widget__content">
                        {tableContainer}
                        {showLinesTotalsFooter && (
                            <SalesOrdersProductLinesTotalsFooter
                                data={data}
                                showTaxDetails={useTaxesSalesOrders}
                            />
                        )}
                    </div>
                    <AddProductsModal
                        onRef={handleProductsModalOnRef}
                        idRate={data.idRate || '-1'}
                        idCompany={data.idCompany?.value || '-1'}
                        idEnvironment={effectiveIdEnvironment || '-1'}
                        idCurrency={data.idCurrency?.value || data.idCurrency}
                        currencySymbol={data.currencySymbol}
                        quoteProductByEnvironment={quoteProductByEnvironment}
                        onAddProducts={onAddProducts}
                    />
                </WidgetContentLayout>
            </WidgetLayout>
        );
    },
);

export default connect(mapStateToProps)(ProductLines);
